import { categories, CATEGORY_LABEL } from "@/mocks/products";
import { useTranslation } from "react-i18next";
import { useLocalized } from "@/lib/localize";
import { useBilingual } from "@/lib/bilingual";

interface Filters {
  category: string;
}

interface FilterSidebarProps {
  filters: Filters;
  onChange: (filters: Filters) => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

const FilterSidebar = ({ filters, onChange, mobileOpen = false, onMobileClose }: FilterSidebarProps) => {
  const { t } = useTranslation();
  const loc = useLocalized();
  const b = useBilingual();

  const handleReset = () => onChange({ category: "all" });

  const labelFor = (id: string) => {
    if (id === "all") return t("cta.viewAll");
    const entry = CATEGORY_LABEL[id];
    return entry ? loc(entry) : id;
  };

  const panelContent = (
    <>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-stone-900 font-bold text-base">{t("common.filters")}</h3>
          <button
            onClick={handleReset}
            className="text-amber-700 text-xs font-medium hover:underline cursor-pointer whitespace-nowrap"
          >
            {t("common.resetAll")}
          </button>
        </div>

        {/* Product line */}
        <div className="mb-6">
          <p className="text-stone-700 text-xs font-semibold uppercase tracking-wide mb-3">
            {t("common.category")}
          </p>
          <div className="space-y-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => onChange({ ...filters, category: cat.id })}
                className={`w-full text-left text-sm px-3 py-2 rounded-lg transition-colors duration-150 cursor-pointer whitespace-nowrap ${
                  filters.category === cat.id
                    ? "bg-amber-700 text-white font-semibold"
                    : "text-stone-600 hover:bg-stone-50"
                }`}
              >
                {labelFor(cat.id)}
              </button>
            ))}
          </div>
        </div>
    </>
  );

  return (
    <>
      {/* Desktop — inline sticky sidebar */}
      <aside className="hidden lg:block w-64 shrink-0">
        <div className="bg-white border border-stone-100 rounded-2xl p-6 sticky top-24">
          {panelContent}
        </div>
      </aside>

      {/* Mobile — full-screen drawer */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 bg-black/50"
          onClick={onMobileClose}
        >
          <div
            className="absolute right-0 top-0 bottom-0 w-[88vw] max-w-sm bg-white overflow-y-auto p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onMobileClose}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-stone-100 text-stone-500 cursor-pointer"
              aria-label={b("Close filters", "關閉篩選")}
            >
              <i className="ri-close-line" />
            </button>
            {panelContent}
            <button
              onClick={onMobileClose}
              className="w-full mt-4 bg-amber-700 text-white font-semibold py-3 rounded-xl text-sm cursor-pointer"
            >
              {b("Apply", "套用")}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default FilterSidebar;
